import type { ReactNode } from 'react';
import type { TodayOutlook } from '../../types/weather';
import { HourlyTempChart } from './HourlyTempChart';
import { HourlyWindChart } from './HourlyWindChart';
import { HourlyPrecipChart } from './HourlyPrecipChart';
import { HourlyPressureChart } from './HourlyPressureChart';
import { MoonIcon } from './MoonIcon';
import { weatherCodeLabel, weatherCodeEmoji } from './weatherCode';

function scoreClasses(label: string): string {
  if (label === 'Excellent') return 'bg-emerald-500/15 text-emerald-400 ring-emerald-500/40';
  if (label === 'Good')      return 'bg-green-500/15 text-green-400 ring-green-500/40';
  if (label === 'Fair')      return 'bg-amber-500/15 text-amber-400 ring-amber-500/40';
  return 'bg-red-500/15 text-red-400 ring-red-500/40';
}

function pressureArrow(trend: string): string {
  if (trend === 'Rising')  return '↑';
  if (trend === 'Falling') return '↓';
  return '→';
}

function cardinal(deg: number): string {
  const dirs = ['N','NE','E','SE','S','SW','W','NW'];
  return dirs[Math.round(((deg % 360) / 45)) % 8];
}

function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className="text-sm font-medium text-slate-800 dark:text-slate-100 leading-tight">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 dark:text-slate-400 leading-tight">{sub}</div>}
    </div>
  );
}

export function TodayHeroCard({ today }: { today: TodayOutlook }) {
  const dateLabel = new Date(today.date + 'T12:00:00')
    .toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

  const conditions = weatherCodeLabel(today.weatherCode);
  const emoji      = weatherCodeEmoji(today.weatherCode, today.isDay);
  const visibility = Math.min(today.visibilityMi, 15);

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-4 mb-4">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-xs font-bold tracking-wider text-slate-500 dark:text-slate-400">
          TODAY · {dateLabel}
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${scoreClasses(today.label)}`}>
          {today.label} <span className="opacity-70">·</span> {today.score}
        </span>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <div className="text-6xl leading-none" aria-hidden="true">{emoji}</div>
        <div className="flex-1">
          <div className="flex items-baseline gap-2">
            <div className="text-5xl font-light text-slate-900 dark:text-slate-50 leading-none">
              {Math.round(today.tempF)}°
            </div>
            <div className="text-base text-slate-600 dark:text-slate-300">
              feels {Math.round(today.apparentTempF)}°
            </div>
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">
            {conditions} · H {Math.round(today.tempHighF)}° / L {Math.round(today.tempLowF)}°
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
        <Stat
          label="Wind"
          value={`${Math.round(today.windMph)} mph ${cardinal(today.windDirDeg)}`}
          sub={`gusts ${Math.round(today.windGustsMph)}`}
        />
        <Stat
          label="Pressure"
          value={`${Math.round(today.pressureHpa)} hPa`}
          sub={<span>{pressureArrow(today.pressureTrend)} {today.pressureTrend}</span>}
        />
        <Stat
          label="Precipitation"
          value={`${today.precipChancePct}%`}
          sub={`cloud ${today.cloudCoverPct}%`}
        />
        <Stat
          label="Humidity"
          value={`${today.humidityPct}%`}
          sub={`dew ${Math.round(today.dewPointF)}°`}
        />
        <Stat
          label="UV Index"
          value={today.uvIndex.toFixed(1)}
          sub={`max ${today.uvIndexMax.toFixed(1)}`}
        />
        <Stat label="Visibility" value={`${visibility.toFixed(1)} mi`} />
        <Stat
          label="Sun"
          value={`${today.sunriseLocal} → ${today.sunsetLocal}`}
          sub={`${today.daylightHours.toFixed(1)}h daylight · ${today.sunshineHours.toFixed(1)}h sun`}
        />
        <Stat
          label="Moon"
          value={
            <span className="inline-flex items-center gap-1.5">
              <MoonIcon phase={today.lunar.phase} size={16} />
              {today.lunar.phaseName}
            </span>
          }
        />
      </div>

      <div className={`rounded-md px-3 py-2 mb-4 text-sm ring-1 ${scoreClasses(today.label)}`}>
        <span className="font-semibold">Best bite:</span> {today.bestPeriodLabel}
      </div>

      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between mb-0.5">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Temp / Feels-like (°F)</div>
          </div>
          <HourlyTempChart hourly={today.hourly} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-0.5">Wind / Gusts (mph)</div>
          <HourlyWindChart hourly={today.hourly} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-0.5">Precipitation (%)</div>
          <HourlyPrecipChart hourly={today.hourly} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-0.5">Pressure (hPa)</div>
          <HourlyPressureChart hourly={today.hourly} />
        </div>
      </div>
    </div>
  );
}
